/**
 * Photo Upload V3 — Core Module
 * @version 3.0.0
 */
(function () {
  if (customElements.get('photo-upload-v3')) return;

  class PhotoUploadV3 extends HTMLElement {
    constructor() {
      super();
      this.files = [];
      this.quantity = 9;
      this.unitPrice = 2.5;
      this.currency = '£';
      this.minQty = 6;
      this.maxQty = 100;
      this.batchSize = 5;
      this.isUploading = false;
      this.uploadAbortController = null;
      this.modalOpen = false;
    }

    connectedCallback() {
      this.quantity = parseInt(this.dataset.defaultQty) || 9;
      this.unitPrice = parseFloat(this.dataset.unitPrice) || 2.5;
      this.currency = this.dataset.currency || '£';
      this.minQty = parseInt(this.dataset.minQty) || 6;
      this.maxQty = parseInt(this.dataset.maxQty) || 100;
      this.variantId = this.dataset.variantId;
      this.workerUrl = (this.dataset.workerUrl || '').trim().replace(/\/$/, '');
      this.workerKey = (this.dataset.workerKey || '').trim();
      this.buttonText = this.dataset.buttonText || 'Upload Pictures';

      this._qtySection = this.closest('.v3-photo-upload-block')?.querySelector('.v3-qty-section');
      this._bindOutsideControls();
      this._renderModal();
      this._bindModalEvents();
    }

    /* ========== OUTSIDE QUANTITY CONTROLS ========== */
    _bindOutsideControls() {
      if (!this._qtySection) return;
      const input = this._qtySection.querySelector('.v3-qty-input');
      const minus = this._qtySection.querySelector('.v3-qty-btn--minus');
      const plus = this._qtySection.querySelector('.v3-qty-btn--plus');
      const trigger = this._qtySection.querySelector('.v3-trigger');

      minus?.addEventListener('click', () => this._changeQty(this.quantity - 1, 'outside'));
      plus?.addEventListener('click', () => this._changeQty(this.quantity + 1, 'outside'));
      input?.addEventListener('change', (e) => this._changeQty(parseInt(e.target.value) || this.minQty, 'outside'));
      trigger?.addEventListener('click', () => this._openModal());
    }

    _changeQty(newVal, source) {
      const old = this.quantity;
      this.quantity = Math.max(this.minQty, Math.min(this.maxQty, newVal));

      // Auto-trim files from end if quantity decreased
      if (this.quantity < old && this.files.length > this.quantity) {
        const before = this.files.length;
        this.files = this.files
          .filter(file => file.slotIndex < this.quantity)
          .sort((a, b) => a.slotIndex - b.slotIndex)
          .slice(0, this.quantity);
        const removed = before - this.files.length;
        this._showTrimWarning(removed);
      }

      // Sync both inputs
      this._syncQtyInputs(source);
      this._updatePricePreview();
      this._updateBulkMessage(newVal);
      if (this.modalOpen) this._renderGrid();
      this._updateAddToCartState();
    }

    _syncQtyInputs(source) {
      // Outside input
      const outsideInput = this._qtySection?.querySelector('.v3-qty-input');
      if (outsideInput && source !== 'outside') outsideInput.value = this.quantity;
      if (outsideInput && source === 'outside') outsideInput.value = this.quantity;
      // Modal input
      const modalInput = this._modal?.querySelector('.v3-sidebar__qty-input');
      if (modalInput) modalInput.value = this.quantity;
    }

    _updatePricePreview() {
      const total = (this.quantity * this.unitPrice).toFixed(2);
      const calcEl = this._qtySection?.querySelector('[data-price-calc]');
      const totalEl = this._qtySection?.querySelector('[data-price-total]');
      if (calcEl) calcEl.textContent = `${this.quantity} × ${this.currency}${this.unitPrice.toFixed(2)}`;
      if (totalEl) totalEl.textContent = `= ${this.currency}${total}`;
      // Modal price
      const mPrice = this._modal?.querySelector('[data-modal-price]');
      if (mPrice) mPrice.textContent = `${this.quantity} × ${this.currency}${this.unitPrice.toFixed(2)} = ${this.currency}${total}`;
    }

    _updateBulkMessage(rawVal) {
      const msg = this._qtySection?.querySelector('.v3-bulk-message');
      if (msg) msg.style.display = rawVal > this.maxQty ? 'flex' : 'none';
    }

    _showTrimWarning(count) {
      const sb = this._modal?.querySelector('.v3-sidebar__warning');
      if (sb) {
        sb.textContent = `${count} photo(s) removed from the end.`;
        sb.style.display = 'block';
        setTimeout(() => { sb.style.display = 'none'; }, 4000);
      }
    }

    /* ========== MODAL RENDERING ========== */
    _renderModal() {
      this._modal = document.createElement('div');
      this._modal.className = 'v3-modal';
      this._modal.innerHTML = `
        <div class="v3-modal__container">
          <div class="v3-modal__header">
            <h2 class="v3-modal__title">Upload Your Photos</h2>
            <button class="v3-modal__close" data-action="close">&times;</button>
          </div>
          <div class="v3-modal__body">
            <div class="v3-step v3-step--upload" data-step="upload">
              <div class="v3-dropzone" data-dropzone>
                <svg class="v3-dropzone__icon" viewBox="0 0 80 80" fill="none" stroke="#bbb" stroke-width="2">
                  <rect x="8" y="8" width="64" height="64" rx="8"/>
                  <circle cx="28" cy="28" r="6"/>
                  <path d="M8 56l20-20 12 12 8-8 24 24"/>
                </svg>
                <p class="v3-dropzone__text">Drag & drop your photos here</p>
                <button type="button" class="v3-pc-btn" data-action="browse">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16">
                    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                    <polyline points="17 8 12 3 7 8"/>
                    <line x1="12" y1="3" x2="12" y2="15"/>
                  </svg>
                  Upload from device
                </button>
              </div>
              <input type="file" class="v3-file-input" accept="image/*" multiple>
            </div>
            <div class="v3-step v3-step--preview" data-step="preview" style="display:none;">
              <div class="v3-preview">
                <div class="v3-preview__wrapper">
                  <div class="v3-grid" data-grid></div>
                  <div class="v3-sidebar">
                    <div class="v3-sidebar__info">
                      <h3>Your Photos</h3>
                      <div class="v3-sidebar__note">
                        <div class="v3-sidebar__note-icon"></div>
                        <span>Each photo = 1 magnet. Photos will be printed in square format.</span>
                      </div>
                      <div class="v3-sidebar__count" data-count-text>0 / ${this.quantity} photos</div>
                      <div class="v3-sidebar__warning" style="display:none;color:#e53e3e;font-size:13px;margin-bottom:8px;"></div>
                    </div>
                    <div class="v3-sidebar__qty">
                      <span class="v3-sidebar__qty-label">Qty:</span>
                      <div class="v3-sidebar__qty-stepper">
                        <button type="button" class="v3-sidebar__qty-btn" data-action="modal-minus">−</button>
                        <input type="number" class="v3-sidebar__qty-input" value="${this.quantity}" min="${this.minQty}" max="${this.maxQty}">
                        <button type="button" class="v3-sidebar__qty-btn" data-action="modal-plus">+</button>
                      </div>
                    </div>
                    <div class="v3-sidebar__price-mini" data-modal-price></div>
                    <div class="v3-sidebar__buttons">
                      <button type="button" class="v3-pc-btn" data-action="add-more" style="width:100%;justify-content:center;">
                        + Add More Photos
                      </button>
                      <button type="button" class="v3-addtocart-btn" data-action="addtocart" disabled>
                        Add to Cart
                      </button>
                      <button type="button" class="v3-cancel-btn" data-action="cancel-upload">
                        Cancel Upload
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="v3-progress" data-progress>
            <div class="v3-progress__bar"><div class="v3-progress__fill" data-progress-fill></div></div>
            <div class="v3-progress__text" data-progress-text>Uploading...</div>
          </div>
        </div>`;
      document.body.appendChild(this._modal);
    }

    _bindModalEvents() {
      const m = this._modal;
      // Close
      m.querySelector('[data-action="close"]').addEventListener('click', () => this._closeModal());
      m.addEventListener('click', (e) => { if (e.target === m) this._closeModal(); });
      // Browse
      const fileInput = m.querySelector('.v3-file-input');
      m.querySelector('[data-action="browse"]').addEventListener('click', () => fileInput.click());
      m.querySelector('[data-action="add-more"]')?.addEventListener('click', () => fileInput.click());
      fileInput.addEventListener('change', (e) => { this._processFiles(e.target.files); e.target.value = ''; });
      // Dropzone
      const dz = m.querySelector('[data-dropzone]');
      dz.addEventListener('dragover', (e) => { e.preventDefault(); dz.classList.add('drag-over'); });
      dz.addEventListener('dragleave', () => dz.classList.remove('drag-over'));
      dz.addEventListener('drop', (e) => { e.preventDefault(); dz.classList.remove('drag-over'); this._processFiles(e.dataTransfer.files); });
      // Modal qty stepper
      m.querySelector('[data-action="modal-minus"]')?.addEventListener('click', () => this._changeQty(this.quantity - 1, 'modal'));
      m.querySelector('[data-action="modal-plus"]')?.addEventListener('click', () => this._changeQty(this.quantity + 1, 'modal'));
      m.querySelector('.v3-sidebar__qty-input')?.addEventListener('change', (e) => this._changeQty(parseInt(e.target.value) || this.minQty, 'modal'));
      // Add to cart
      m.querySelector('[data-action="addtocart"]')?.addEventListener('click', () => this._handleAddToCart());
      // Cancel
      m.querySelector('[data-action="cancel-upload"]')?.addEventListener('click', () => this._cancelUpload());
      // ESC key
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && this.modalOpen) this._closeModal(); });
    }

    _openModal() {
      this.modalOpen = true;
      this._modal.classList.add('active');
      document.body.style.overflow = 'hidden';
      if (this.files.length > 0) {
        this._showStep('preview');
        this._renderGrid();
      } else {
        this._showStep('upload');
      }
      this._updatePricePreview();
    }

    _closeModal() {
      if (this.isUploading) return;
      this.modalOpen = false;
      this._modal.classList.remove('active');
      document.body.style.overflow = '';
    }

    _showStep(step) {
      this._modal.querySelector('[data-step="upload"]').style.display = step === 'upload' ? '' : 'none';
      this._modal.querySelector('[data-step="preview"]').style.display = step === 'preview' ? '' : 'none';
    }

    _applyGridLayoutStyles(grid) {
      if (!grid) return;
      grid.style.removeProperty('column-gap');
      grid.style.removeProperty('row-gap');
      grid.style.removeProperty('gap');
      grid.style.removeProperty('--v3-slot-gap');
    }

    _applySlotLayoutStyles(slot) {
      if (!slot) return;
      slot.style.removeProperty('width');
      slot.style.removeProperty('margin');
      slot.style.removeProperty('aspect-ratio');
      slot.style.removeProperty('position');
    }

    _applySlotImageLayoutStyles(slot) {
      const image = slot?.querySelector('img');
      if (!image) return;
      image.style.removeProperty('position');
      image.style.removeProperty('inset');
      image.style.removeProperty('width');
      image.style.removeProperty('height');
      image.style.removeProperty('max-width');
      image.style.removeProperty('max-height');
      image.style.removeProperty('object-fit');
    }

    /* ========== FILE PROCESSING ========== */
    _processFiles(fileList) {
      const maxSize = 10 * 1024 * 1024; // Match Worker upload limit
      const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/bmp', 'image/tiff'];
      const newFiles = Array.from(fileList).filter(f => {
        if (!allowed.includes(f.type)) return false;
        if (f.size > maxSize) return false;
        return true;
      });

      if (newFiles.length === 0) return;

      const slotsAvailable = this.quantity - this.files.length;
      const toProcess = newFiles.slice(0, slotsAvailable);

      let processed = 0;
      toProcess.forEach(file => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const id = `f_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
          // Find first empty slot
          let slotIndex = 0;
          const usedSlots = new Set(this.files.map(f => f.slotIndex));
          while (usedSlots.has(slotIndex)) slotIndex++;

          this.files.push({
            id, file, dataUrl: e.target.result,
            slotIndex, uploaded: false, uploadUrl: null
          });
          processed++;
          if (processed === toProcess.length) {
            this._showStep('preview');
            this._renderGrid();
            this._updateAddToCartState();
          }
        };
        reader.readAsDataURL(file);
      });
    }

    /* ========== GRID RENDERING ========== */
    _renderGrid() {
      const grid = this._modal.querySelector('[data-grid]');
      this._applyGridLayoutStyles(grid);
      grid.innerHTML = '';

      for (let i = 0; i < this.quantity; i++) {
        const fileObj = this.files.find(f => f.slotIndex === i);
        const slot = document.createElement('div');
        this._applySlotLayoutStyles(slot);

        if (fileObj) {
          slot.className = 'v3-slot v3-slot--filled';
          slot.draggable = true;
          slot.dataset.fileId = fileObj.id;
          slot.dataset.slotIndex = i;
          slot.innerHTML = `
            <img src="${fileObj.dataUrl}" alt="Photo ${i + 1}" loading="lazy">
            <div class="v3-slot__actions">
              <button class="v3-slot__action v3-slot__action--delete" data-action="delete" data-id="${fileObj.id}" title="Delete">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
              </button>
              <button class="v3-slot__action" data-action="crop" data-id="${fileObj.id}" title="Crop / resize">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6.13 1L6 16a2 2 0 002 2h15"/><path d="M1 6.13L16 6a2 2 0 012 2v15"/></svg>
              </button>
            </div>`;
          this._applySlotImageLayoutStyles(slot);
          // Drag events
          slot.addEventListener('dragstart', (e) => { e.dataTransfer.setData('text/plain', fileObj.id); slot.classList.add('v3-slot--dragging'); });
          slot.addEventListener('dragend', () => slot.classList.remove('v3-slot--dragging'));
        } else {
          slot.className = 'v3-slot v3-slot--empty';
          slot.dataset.slotIndex = i;
          slot.innerHTML = `<span class="v3-slot__number">${i + 1}</span><span class="v3-slot__plus">+</span>`;
          slot.addEventListener('click', () => this._modal.querySelector('.v3-file-input').click());
        }

        // Drop target
        slot.addEventListener('dragover', (e) => { e.preventDefault(); slot.classList.add('v3-slot--drag-over'); });
        slot.addEventListener('dragleave', () => slot.classList.remove('v3-slot--drag-over'));
        slot.addEventListener('drop', (e) => {
          e.preventDefault(); slot.classList.remove('v3-slot--drag-over');
          const dragId = e.dataTransfer.getData('text/plain');
          const targetIdx = parseInt(slot.dataset.slotIndex);
          this._swapSlots(dragId, targetIdx);
        });

        grid.appendChild(slot);
      }

      // Bind action buttons
      grid.querySelectorAll('[data-action="delete"]').forEach(btn => {
        btn.addEventListener('click', (e) => { e.stopPropagation(); this._deleteFile(btn.dataset.id); });
      });
      grid.querySelectorAll('[data-action="crop"]').forEach(btn => {
        btn.addEventListener('click', (e) => { e.stopPropagation(); this._openCropModal(btn.dataset.id); });
      });

      // Update count
      const countEl = this._modal.querySelector('[data-count-text]');
      if (countEl) countEl.innerHTML = `<strong>${this.files.length}</strong> / ${this.quantity} photos`;
    }

    _deleteFile(id) {
      this.files = this.files.filter(f => f.id !== id);
      this._renderGrid();
      this._updateAddToCartState();
      if (this.files.length === 0) this._showStep('upload');
    }

    _fillAll(sourceId) {
      const source = this.files.find(f => f.id === sourceId);
      if (!source) return;
      const usedSlots = new Set(this.files.map(f => f.slotIndex));
      for (let i = 0; i < this.quantity; i++) {
        if (!usedSlots.has(i)) {
          this.files.push({
            id: `f_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
            file: source.file, dataUrl: source.dataUrl,
            slotIndex: i, uploaded: false, uploadUrl: null
          });
        }
      }
      this._renderGrid();
      this._updateAddToCartState();
    }

    _swapSlots(dragId, targetIdx) {
      const dragFile = this.files.find(f => f.id === dragId);
      const targetFile = this.files.find(f => f.slotIndex === targetIdx);
      if (!dragFile) return;
      const oldIdx = dragFile.slotIndex;
      dragFile.slotIndex = targetIdx;
      if (targetFile) targetFile.slotIndex = oldIdx;
      this._renderGrid();
    }

    _openCropModal(fileId) {
      const fileObj = this.files.find(f => f.id === fileId);
      if (!fileObj) return;

      const cropModal = document.createElement('div');
      cropModal.className = 'v3-crop-modal';
      cropModal.innerHTML = `
        <div class="v3-crop-modal__overlay"></div>
        <div class="v3-crop-modal__container">
          <div class="v3-crop-modal__header">
            <h3>Crop Image</h3>
            <button type="button" class="v3-crop-modal__close">&times;</button>
          </div>
          <div class="v3-crop-modal__body">
            <div class="v3-crop-modal__canvas-wrapper">
              <canvas class="v3-crop-modal__canvas"></canvas>
              <div class="v3-crop-modal__crop-area">
                <div class="v3-crop-modal__resize-handle v3-crop-modal__resize-handle--nw" data-handle="nw"></div>
                <div class="v3-crop-modal__resize-handle v3-crop-modal__resize-handle--ne" data-handle="ne"></div>
                <div class="v3-crop-modal__resize-handle v3-crop-modal__resize-handle--sw" data-handle="sw"></div>
                <div class="v3-crop-modal__resize-handle v3-crop-modal__resize-handle--se" data-handle="se"></div>
              </div>
            </div>
            <div class="v3-crop-modal__toolbar">
              <button type="button" class="v3-crop-modal__tool-btn" data-tool="flip-h" title="Flip Horizontal">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="20" height="20"><path d="M16 3l4 4-4 4"/><path d="M20 7H4"/><path d="M8 21l-4-4 4-4"/><path d="M4 17h16"/></svg>
                <span>Flip H</span>
              </button>
              <button type="button" class="v3-crop-modal__tool-btn" data-tool="flip-v" title="Flip Vertical">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="20" height="20"><path d="M3 8l4-4 4 4"/><path d="M7 4v16"/><path d="M21 16l-4 4-4-4"/><path d="M17 20V4"/></svg>
                <span>Flip V</span>
              </button>
              <button type="button" class="v3-crop-modal__tool-btn" data-tool="rotate-cw" title="Rotate 90 degrees">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="20" height="20"><path d="M23 4v6h-6"/><path d="M20.49 15a9 9 0 11-2.12-9.36L23 10"/></svg>
                <span>Rotate</span>
              </button>
            </div>
          </div>
          <div class="v3-crop-modal__footer">
            <button type="button" class="v3-crop-modal__cancel">Cancel</button>
            <button type="button" class="v3-crop-modal__apply">Apply Crop</button>
          </div>
        </div>
      `;

      document.body.appendChild(cropModal);

      let flipH = false;
      let flipV = false;
      let rotation = 0;
      let objectUrl = null;
      const img = new Image();

      if (fileObj.file) objectUrl = URL.createObjectURL(fileObj.file);

      img.onload = () => {
        const canvas = cropModal.querySelector('.v3-crop-modal__canvas');
        const ctx = canvas.getContext('2d');
        const wrapper = cropModal.querySelector('.v3-crop-modal__canvas-wrapper');
        const cropArea = cropModal.querySelector('.v3-crop-modal__crop-area');
        const maxSize = window.innerWidth <= 767 ? 400 : 600;
        const minCropSize = 50;

        let displayWidth = 0;
        let displayHeight = 0;
        let cropSize = 0;
        let cropX = 0;
        let cropY = 0;

        const getDisplayDims = () => {
          const isRotated = rotation === 90 || rotation === 270;
          const srcW = isRotated ? img.height : img.width;
          const srcH = isRotated ? img.width : img.height;
          let dw = srcW;
          let dh = srcH;

          if (dw > maxSize || dh > maxSize) {
            const ratio = Math.min(maxSize / dw, maxSize / dh);
            dw = Math.round(srcW * ratio);
            dh = Math.round(srcH * ratio);
          }

          return { displayWidth: dw, displayHeight: dh };
        };

        const updateCropAreaUI = () => {
          cropArea.style.width = `${cropSize}px`;
          cropArea.style.height = `${cropSize}px`;
          cropArea.style.left = `${cropX}px`;
          cropArea.style.top = `${cropY}px`;
        };

        const resetCropArea = () => {
          cropSize = Math.min(displayWidth, displayHeight);
          cropX = (displayWidth - cropSize) / 2;
          cropY = (displayHeight - cropSize) / 2;
          updateCropAreaUI();
        };

        const redrawCanvas = (resetCrop = false) => {
          const dims = getDisplayDims();
          displayWidth = dims.displayWidth;
          displayHeight = dims.displayHeight;
          canvas.width = displayWidth;
          canvas.height = displayHeight;
          wrapper.style.width = `${displayWidth}px`;
          wrapper.style.height = `${displayHeight}px`;

          ctx.save();
          ctx.clearRect(0, 0, displayWidth, displayHeight);
          ctx.translate(displayWidth / 2, displayHeight / 2);
          ctx.rotate((rotation * Math.PI) / 180);
          ctx.scale(flipH ? -1 : 1, flipV ? -1 : 1);

          const isRotated = rotation === 90 || rotation === 270;
          const drawW = isRotated ? displayHeight : displayWidth;
          const drawH = isRotated ? displayWidth : displayHeight;
          ctx.drawImage(img, -drawW / 2, -drawH / 2, drawW, drawH);
          ctx.restore();

          if (resetCrop) resetCropArea();
        };

        redrawCanvas(true);

        cropModal.querySelector('[data-tool="flip-h"]').addEventListener('click', () => {
          flipH = !flipH;
          redrawCanvas();
        });
        cropModal.querySelector('[data-tool="flip-v"]').addEventListener('click', () => {
          flipV = !flipV;
          redrawCanvas();
        });
        cropModal.querySelector('[data-tool="rotate-cw"]').addEventListener('click', () => {
          rotation = (rotation + 90) % 360;
          redrawCanvas(true);
        });

        let isDragging = false;
        let isResizing = false;
        let resizeHandle = null;
        let startX = 0;
        let startY = 0;
        let startCropX = 0;
        let startCropY = 0;
        let startCropSize = 0;

        const getPoint = event => event.touches ? event.touches[0] : event;

        const handleCropAreaDown = event => {
          if (event.target.dataset.handle) return;
          const point = getPoint(event);
          isDragging = true;
          startX = point.clientX;
          startY = point.clientY;
          startCropX = cropX;
          startCropY = cropY;
          event.preventDefault();
        };

        cropArea.addEventListener('mousedown', handleCropAreaDown);
        cropArea.addEventListener('touchstart', handleCropAreaDown, { passive: false });

        cropModal.querySelectorAll('.v3-crop-modal__resize-handle').forEach(handle => {
          const handleResizeDown = event => {
            const point = getPoint(event);
            isResizing = true;
            resizeHandle = event.currentTarget.dataset.handle;
            startX = point.clientX;
            startY = point.clientY;
            startCropX = cropX;
            startCropY = cropY;
            startCropSize = cropSize;
            event.preventDefault();
            event.stopPropagation();
          };
          handle.addEventListener('mousedown', handleResizeDown);
          handle.addEventListener('touchstart', handleResizeDown, { passive: false });
        });

        const handlePointerMove = event => {
          if (!isDragging && !isResizing) return;
          const point = getPoint(event);

          if (isDragging) {
            const dx = point.clientX - startX;
            const dy = point.clientY - startY;
            cropX = Math.max(0, Math.min(displayWidth - cropSize, startCropX + dx));
            cropY = Math.max(0, Math.min(displayHeight - cropSize, startCropY + dy));
            updateCropAreaUI();
          } else if (isResizing) {
            const dx = point.clientX - startX;
            const dy = point.clientY - startY;
            const delta = Math.abs(dx) > Math.abs(dy) ? dx : dy;
            let newSize = cropSize;
            let newX = cropX;
            let newY = cropY;

            switch (resizeHandle) {
              case 'se':
                newSize = Math.max(minCropSize, Math.min(startCropSize + delta, displayWidth - startCropX, displayHeight - startCropY));
                newX = startCropX;
                newY = startCropY;
                break;
              case 'sw':
                newSize = Math.max(minCropSize, Math.min(startCropSize - dx, startCropX + startCropSize, displayHeight - startCropY));
                newX = startCropX + startCropSize - newSize;
                newY = startCropY;
                break;
              case 'ne':
                newSize = Math.max(minCropSize, Math.min(startCropSize + dx, displayWidth - startCropX, startCropY + startCropSize));
                newX = startCropX;
                newY = startCropY + startCropSize - newSize;
                break;
              case 'nw':
                newSize = Math.max(minCropSize, Math.min(startCropSize - delta, startCropX + startCropSize, startCropY + startCropSize));
                newX = startCropX + startCropSize - newSize;
                newY = startCropY + startCropSize - newSize;
                break;
            }

            if (newX >= 0 && newY >= 0 && newX + newSize <= displayWidth && newY + newSize <= displayHeight) {
              cropSize = newSize;
              cropX = newX;
              cropY = newY;
              updateCropAreaUI();
            }
          }

          event.preventDefault();
        };

        const handlePointerUp = () => {
          isDragging = false;
          isResizing = false;
          resizeHandle = null;
        };

        document.addEventListener('mousemove', handlePointerMove);
        document.addEventListener('mouseup', handlePointerUp);
        document.addEventListener('touchmove', handlePointerMove, { passive: false });
        document.addEventListener('touchend', handlePointerUp);

        const cleanup = () => {
          document.removeEventListener('mousemove', handlePointerMove);
          document.removeEventListener('mouseup', handlePointerUp);
          document.removeEventListener('touchmove', handlePointerMove);
          document.removeEventListener('touchend', handlePointerUp);
          if (objectUrl) URL.revokeObjectURL(objectUrl);
          cropModal.remove();
        };

        cropModal.querySelector('.v3-crop-modal__apply').addEventListener('click', () => {
          const isRotated = rotation === 90 || rotation === 270;
          const fullW = isRotated ? img.height : img.width;
          const fullH = isRotated ? img.width : img.height;
          const transformCanvas = document.createElement('canvas');
          transformCanvas.width = fullW;
          transformCanvas.height = fullH;
          const transformCtx = transformCanvas.getContext('2d');

          transformCtx.translate(fullW / 2, fullH / 2);
          transformCtx.rotate((rotation * Math.PI) / 180);
          transformCtx.scale(flipH ? -1 : 1, flipV ? -1 : 1);
          const drawW = isRotated ? fullH : fullW;
          const drawH = isRotated ? fullW : fullH;
          transformCtx.drawImage(img, -drawW / 2, -drawH / 2, drawW, drawH);

          const scaleX = fullW / displayWidth;
          const scaleY = fullH / displayHeight;
          const actualX = cropX * scaleX;
          const actualY = cropY * scaleY;
          const actualSize = cropSize * Math.max(scaleX, scaleY);

          const cropCanvas = document.createElement('canvas');
          cropCanvas.width = actualSize;
          cropCanvas.height = actualSize;
          const cropCtx = cropCanvas.getContext('2d');
          cropCtx.drawImage(transformCanvas, actualX, actualY, actualSize, actualSize, 0, 0, actualSize, actualSize);

          const tempImg = new Image();
          tempImg.onload = () => {
            fileObj.dataUrl = this._compressImage(tempImg, 800, 0.85);
            fileObj.uploaded = false;
            fileObj.uploadUrl = null;
            cleanup();
            this._renderGrid();
            this._updateAddToCartState();
          };
          tempImg.src = cropCanvas.toDataURL('image/jpeg', 0.9);
        });

        cropModal.querySelector('.v3-crop-modal__close').addEventListener('click', cleanup);
        cropModal.querySelector('.v3-crop-modal__cancel').addEventListener('click', cleanup);
        cropModal.querySelector('.v3-crop-modal__overlay').addEventListener('click', cleanup);
      };

      img.src = objectUrl || fileObj.dataUrl;
    }

    _compressImage(img, maxSize, quality) {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      let width = img.width;
      let height = img.height;

      if (width > height && width > maxSize) {
        height = Math.round((height * maxSize) / width);
        width = maxSize;
      } else if (height > maxSize) {
        width = Math.round((width * maxSize) / height);
        height = maxSize;
      }

      canvas.width = width;
      canvas.height = height;
      ctx.drawImage(img, 0, 0, width, height);
      return canvas.toDataURL('image/jpeg', quality);
    }

    _compressForCart(dataUrl) {
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(this._compressImage(img, 400, 0.6));
        img.onerror = () => resolve(dataUrl);
        img.src = dataUrl;
      });
    }

    _getWorkerUploadUrl() {
      if (!this.workerUrl) return '';
      return this.workerUrl.endsWith('/upload') ? this.workerUrl : `${this.workerUrl}/upload`;
    }

    _updateAddToCartState() {
      const btn = this._modal?.querySelector('[data-action="addtocart"]');
      if (btn) btn.disabled = this.files.length < this.quantity || this.isUploading;
    }

    /* ========== UPLOAD PIPELINE ========== */
    async _handleAddToCart() {
      if (this.files.length < this.quantity) return;
      this.isUploading = true;
      this._updateAddToCartState();
      this._showProgress(true);
      this._modal.querySelector('.v3-cancel-btn').style.display = 'block';
      this.uploadAbortController = new AbortController();

      try {
        if (this.workerUrl) {
          await this._uploadToWorker();
        }
        await this._submitToCart();
        this._showSuccess();
      } catch (err) {
        if (err.name === 'AbortError') {
          console.log('Upload cancelled');
        } else {
          console.error('Upload failed:', err);
          alert('Upload failed. Please try again.');
        }
      } finally {
        this.isUploading = false;
        this._showProgress(false);
        this._modal.querySelector('.v3-cancel-btn').style.display = 'none';
        this._updateAddToCartState();
      }
    }

    async _uploadToWorker() {
      const toUpload = this.files.filter(f => !f.uploaded);
      const total = toUpload.length;
      let done = 0;
      const uploadUrl = this._getWorkerUploadUrl();
      if (!uploadUrl) return;

      for (let i = 0; i < total; i += this.batchSize) {
        if (this.uploadAbortController.signal.aborted) throw new DOMException('Aborted', 'AbortError');
        const batch = toUpload.slice(i, i + this.batchSize);
        await Promise.all(batch.map(async (fileObj) => {
          try {
            const formData = new FormData();
            const fileName = fileObj.file?.name || `${fileObj.id}.jpg`;
            formData.append('file', fileObj.file, fileName);
            const resp = await fetch(uploadUrl, {
              method: 'POST', body: formData,
              headers: this.workerKey ? { 'X-API-Key': this.workerKey } : {},
              signal: this.uploadAbortController.signal
            });
            if (resp.ok) {
              const data = await resp.json();
              fileObj.uploaded = true;
              fileObj.uploadUrl = data.url || data.publicUrl;
            } else {
              const errText = await resp.text();
              console.warn('[PhotoUploadV3] Worker upload error:', resp.status, errText);
            }
          } catch (e) {
            if (e.name === 'AbortError') throw e;
            console.warn('[PhotoUploadV3] Worker upload failed:', e);
          }
          done++;
          this._setProgress(Math.round((done / total) * 100));
        }));
      }
    }

    async _submitToCart() {
      const uploadId = `v3_${Date.now()}`;
      const properties = {
        _photo_count: String(this.files.length),
        _upload_id: uploadId,
      };

      // Add uploaded gallery URLs if the worker returned remote URLs.
      const uploadedUrls = this.files.filter(f => f.uploadUrl).map(f => f.uploadUrl);
      if (uploadedUrls.length > 0) {
        properties._gallery_urls = JSON.stringify(uploadedUrls);
      }

      const sortedFiles = this.files
        .slice()
        .sort((a, b) => a.slotIndex - b.slotIndex);

      for (let index = 0; index < sortedFiles.length; index++) {
        const fileObj = sortedFiles[index];
        properties[`_Photo ${index + 1}`] = fileObj.uploadUrl || await this._compressForCart(fileObj.dataUrl);
      }

      // Fallback: store base64 thumbnails for order reference
      if (uploadedUrls.length === 0) {
        properties._upload_method = 'local';
      }

      const body = {
        id: parseInt(this.variantId),
        quantity: this.quantity,
        properties
      };

      const resp = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      const text = await resp.text();
      let result = null;
      try {
        result = text ? JSON.parse(text) : null;
      } catch (e) {
        result = null;
      }

      if (!resp.ok) {
        const message = result?.description || result?.message || text || `Cart error: ${resp.status}`;
        throw new Error(message);
      }

      return result;
    }

    _cancelUpload() {
      this.uploadAbortController?.abort();
    }

    _showProgress(show) {
      const el = this._modal.querySelector('[data-progress]');
      if (el) el.classList.toggle('active', show);
      if (show) this._setProgress(0);
    }

    _setProgress(pct) {
      const fill = this._modal.querySelector('[data-progress-fill]');
      const text = this._modal.querySelector('[data-progress-text]');
      if (fill) fill.style.width = pct + '%';
      if (text) text.textContent = `Uploading... ${pct}%`;
    }

    /* ========== SUCCESS ========== */
    _showSuccess() {
      this._closeModal();
      this.files = [];
      // Update trigger button state
      const trigger = this._qtySection?.querySelector('.v3-trigger');
      if (trigger) {
        trigger.classList.add('v3-trigger--filled');
        trigger.querySelector('span').textContent = '✓ Photos Added';
      }
      // Update cart bubble
      fetch('/cart.js').then(r => r.json()).then(cart => {
        document.querySelectorAll('.m-cart-count, [data-cart-count]').forEach(el => {
          el.textContent = cart.item_count;
        });
      });
      // Simple success alert (can upgrade to popup later)
      const popup = document.createElement('div');
      popup.className = 'v3-success';
      popup.innerHTML = `
        <button class="v3-success__close">&times;</button>
        <p class="v3-success__message">🎉 ${this.quantity} photo magnets added to cart!</p>
        <div class="v3-success__buttons">
          <a href="/cart" class="v3-success__btn v3-success__btn--primary">View Cart</a>
          <button class="v3-success__btn v3-success__btn--secondary" data-dismiss>Continue Shopping</button>
        </div>`;
      document.body.appendChild(popup);
      const overlay = document.createElement('div');
      overlay.style.cssText = 'position:fixed;inset:0;z-index:9999;background:rgba(0,0,0,0.4);';
      document.body.insertBefore(overlay, popup);
      const dismiss = () => { popup.remove(); overlay.remove(); };
      popup.querySelector('.v3-success__close').addEventListener('click', dismiss);
      popup.querySelector('[data-dismiss]').addEventListener('click', dismiss);
      overlay.addEventListener('click', dismiss);
    }

    disconnectedCallback() {
      this._modal?.remove();
    }
  }

  customElements.define('photo-upload-v3', PhotoUploadV3);
})();
